import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Stack,
} from "@mui/material";
import DOMPurify from "dompurify";
import { EmailMessage, EmailAttachment } from "@services/documents/email/types";
import { EmailAnnotationComponent } from "./EmailAnnotationComponent";
import { TinyValueComponent } from "../components/TinyValueComponent";
import { EmailAttachmentChip } from "./EmailAttachmentChip";
import { ForwardedEmailsSection } from "./ForwardedEmailsSection";

interface EmailMessageComponentProps {
  message: EmailMessage;
  onSelectForwardedEmail: (email: EmailMessage | null) => void;
  onRetryMessage?: () => void;
  showMessageIds?: boolean;
}

export const EmailMessageComponent: React.FC<EmailMessageComponentProps> = ({
  message,
  onSelectForwardedEmail,
  showMessageIds,
}) => {
  return (
    <Card key={message.messageId} variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        {/* ID Chips at top right */}
        {showMessageIds && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Stack direction="row" spacing={0.5}>
              <TinyValueComponent
                prefix="tid:"
                value={message.threadId}
                cropText={true}
              />
              <TinyValueComponent
                prefix="mid:"
                value={message.messageId}
                cropText={true}
              />
              {message.headerMessageId && (
                <TinyValueComponent
                  prefix="hmid:"
                  value={message.headerMessageId}
                  cropText={true}
                />
              )}
            </Stack>
          </Box>
        )}

        {/* Sender & Recipients */}
        <Typography variant="body2" color="textSecondary">
          <strong>From:</strong> {message.fromEmail}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          <strong>To:</strong> {message.toEmails.join(", ")}
        </Typography>
        {message.ccEmails && message.ccEmails.length > 0 && (
          <Typography variant="body2" color="textSecondary">
            <strong>CC:</strong> {message.ccEmails.join(", ")}
          </Typography>
        )}
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {new Date(message.date).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Typography>

        {/* Email Annotation */}
        <EmailAnnotationComponent messageId={message.messageId} />

        {/* Attachments */}
        {message.attachments.length > 0 && (
          <Box
            mb={2}
            p={1}
            sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
          >
            <Typography variant="body2" fontWeight="bold" mb={1}>
              Attachments:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {message.attachments.map((attachment: EmailAttachment) => (
                <EmailAttachmentChip
                  key={attachment.filename}
                  attachment={attachment}
                  messageId={message.messageId}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Forwarded Emails */}
        <ForwardedEmailsSection
          mailAttachments={message.mailAttachments || []}
          onSelectForwardedEmail={onSelectForwardedEmail}
        />
        <Divider sx={{ my: 2 }} />
        {/* Email Body */}
        <Box className="prose max-w-none" sx={{ overflowX: "auto" }}>
          {message.htmlBody || message.body ? (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  message.htmlBody ||
                    // Convert plain text newlines to <br> tags
                    message.body?.replace(/\n/g, "<br>") ||
                    "",
                  {
                    ADD_ATTR: ["src", "href", "target", "rel"],
                    ADD_TAGS: [
                      "img",
                      "a",
                      "p",
                      "div",
                      "span",
                      "br",
                      "ul",
                      "ol",
                      "li",
                      "strong",
                      "b",
                      "em",
                      "i",
                      "u",
                      "h1",
                      "h2",
                      "h3",
                      "h4",
                      "h5",
                      "h6",
                      "table",
                      "tr",
                      "td",
                      "th",
                      "tbody",
                      "thead",
                    ],
                    FORBID_TAGS: ["script", "style"],
                  }
                ),
              }}
            />
          ) : (
            <CircularProgress size={24} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
